import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import comptabiliteService, { Exercice } from '../../api/comptabilite';
import { formatTND } from '../../utils/formatters';

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const endOfYear = () => `${new Date().getFullYear()}-12-31`;

const Exercices: React.FC = () => {
  const [exercices, setExercices] = useState<Exercice[]>([]);
  const [libelle, setLibelle] = useState('');
  const [dateDebut, setDateDebut] = useState(startOfYear());
  const [dateFin, setDateFin] = useState(endOfYear());
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      setExercices(await comptabiliteService.listExercices());
    } catch (e: any) {
      if (e?.response?.status !== 403) toast.error('Chargement des exercices impossible.');
    }
  };

  useEffect(() => { refresh(); }, []);

  const creer = async () => {
    setBusy(true);
    try {
      await comptabiliteService.createExercice({ libelle: libelle || undefined, dateDebut, dateFin });
      toast.success('Exercice créé.');
      setLibelle('');
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Création impossible.');
    } finally { setBusy(false); }
  };

  const cloturer = async (ex: Exercice) => {
    if (!window.confirm(`Clôturer « ${ex.libelle} » ? Le résultat sera regroupé et les à-nouveaux générés.`)) return;
    try {
      const res = await comptabiliteService.cloturerExercice(ex.id);
      toast.success(`Exercice clôturé. Résultat : ${formatTND(res.resultat)}.`);
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Clôture impossible.');
    }
  };

  const reouvrir = async (ex: Exercice) => {
    if (!window.confirm(`Ré-ouvrir « ${ex.libelle} » ? Les écritures de clôture et d'à-nouveaux seront supprimées.`)) return;
    try {
      await comptabiliteService.reouvrirExercice(ex.id);
      toast.success('Exercice ré-ouvert.');
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Ré-ouverture impossible.');
    }
  };

  const dotations = async (ex: Exercice) => {
    try {
      const res = await comptabiliteService.genererDotations(ex.id);
      if (res.immobilisationsTraitees === 0) toast('Aucune dotation à générer sur cet exercice.');
      else toast.success(`${res.immobilisationsTraitees} dotation(s) — total ${formatTND(res.totalDotation)}.`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Génération des dotations impossible.');
    }
  };

  const supprimer = async (ex: Exercice) => {
    if (!window.confirm(`Supprimer l'exercice « ${ex.libelle} » ?`)) return;
    try {
      await comptabiliteService.deleteExercice(ex.id);
      refresh();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Suppression impossible.');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <Link to="/comptabilite" className="text-sm text-blue-600 hover:underline">← Comptabilité</Link>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">Exercices &amp; clôture</h1>
        <p className="text-sm text-gray-500">
          Verrouillez les périodes, déterminez le résultat et reportez les soldes à-nouveau.
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Nouvel exercice</h2>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Libellé</label>
            <input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Exercice 2026"
                   className="rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Du</label>
            <input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)}
                   className="rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Au</label>
            <input type="date" value={dateFin} onChange={(e) => setDateFin(e.target.value)}
                   className="rounded-lg border border-gray-200 px-3 py-2" />
          </div>
          <button onClick={creer} disabled={busy}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50">
            Créer
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left px-4 py-3">Exercice</th>
              <th className="text-left px-4 py-3">Période</th>
              <th className="text-left px-4 py-3">Statut</th>
              <th className="text-right px-4 py-3">Résultat</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {exercices.map((ex) => (
              <tr key={ex.id} className="border-t border-gray-100">
                <td className="px-4 py-3 font-medium text-gray-900">{ex.libelle}</td>
                <td className="px-4 py-3 text-gray-600">{ex.dateDebut} → {ex.dateFin}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    ex.statut === 'CLOTURE' ? 'bg-gray-200 text-gray-700' : 'bg-green-100 text-green-700'}`}>
                    {ex.statut === 'CLOTURE' ? 'Clôturé' : 'Ouvert'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">{ex.resultat != null ? formatTND(ex.resultat) : '—'}</td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  {ex.statut === 'OUVERT' ? (
                    <>
                      <button onClick={() => dotations(ex)} className="text-amber-600 hover:underline">Dotations</button>
                      <button onClick={() => cloturer(ex)} className="text-blue-600 hover:underline">Clôturer</button>
                      <button onClick={() => supprimer(ex)} className="text-red-600 hover:underline">Suppr.</button>
                    </>
                  ) : (
                    <button onClick={() => reouvrir(ex)} className="text-blue-600 hover:underline">Ré-ouvrir</button>
                  )}
                </td>
              </tr>
            ))}
            {exercices.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Aucun exercice.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Exercices;
